import type { IconName } from '../components/Icon';

export type IndustryGroupId =
    | 'building'
    | 'home-services'
    | 'events'
    | 'business';

export interface IndustryGroup {
    id: IndustryGroupId;
    label: string;
}

export interface Industry {
    /** ID stable stocké en DB — ne pas renommer une fois déployé */
    id: string;
    label: string;
    group: IndustryGroupId;
    icon: IconName;
}

export const INDUSTRY_GROUPS: IndustryGroup[] = [
    { id: 'building',       label: 'Bâtiment & habitat' },
    { id: 'home-services',  label: 'Services aux particuliers' },
    { id: 'events',         label: 'Événementiel & créatif' },
    { id: 'business',       label: 'Professionnels & B2B' },
];

/**
 * Liste des secteurs supportés par Quotio.
 * Organisée pour couvrir la majorité des TPE/PME qui émettent des devis.
 * L'`id` est l'identifiant stable (persisté en DB) ; `icon` est affiché.
 *
 * ⚠️ Ne pas renommer un `id` existant une fois que des orgs/générateurs
 * ont été créés avec. Pour renommer, ajoute un nouvel id et migre.
 */
export const INDUSTRIES: Industry[] = [
    // ---------- Bâtiment & habitat ----------
    { id: 'construction', label: 'Construction & rénovation', group: 'building', icon: 'renovation' },
    { id: 'carpentry',    label: 'Menuiserie & ébénisterie',  group: 'building', icon: 'carpentry' },
    { id: 'electrician',  label: 'Électricité',               group: 'building', icon: 'electricity' },
    { id: 'plumbing',     label: 'Plomberie & sanitaire',     group: 'building', icon: 'plumbing' },
    { id: 'hvac',         label: 'Climatisation & chauffage', group: 'building', icon: 'hvac' },
    { id: 'painting',     label: 'Peinture & revêtements',    group: 'building', icon: 'painting' },
    { id: 'roofing',      label: 'Toiture & couverture',      group: 'building', icon: 'roofing' },
    { id: 'landscaping',  label: 'Paysagisme & jardinage',    group: 'building', icon: 'landscaping' },
    { id: 'pool',         label: 'Piscine & spa',             group: 'building', icon: 'pool' },

    // ---------- Services aux particuliers ----------
    { id: 'cleaning',     label: 'Nettoyage & entretien',     group: 'home-services', icon: 'cleaning' },
    { id: 'moving',       label: 'Déménagement',              group: 'home-services', icon: 'moving' },
    { id: 'home-care',    label: 'Aide à domicile & garde',   group: 'home-services', icon: 'home-care' },
    { id: 'beauty',       label: 'Beauté & bien-être',        group: 'home-services', icon: 'beauty' },
    { id: 'automotive',   label: 'Automobile',                group: 'home-services', icon: 'automotive' },

    // ---------- Événementiel & créatif ----------
    { id: 'catering',     label: 'Traiteur & restauration',   group: 'events', icon: 'catering' },
    { id: 'photo',        label: 'Photo & vidéo',             group: 'events', icon: 'photo' },
    { id: 'music',        label: 'DJ, musique & animation',   group: 'events', icon: 'music' },
    { id: 'florist',      label: 'Fleuriste & décoration',    group: 'events', icon: 'florist' },

    // ---------- Professionnels & B2B ----------
    { id: 'consulting',   label: 'Conseil & freelance',       group: 'business', icon: 'consulting' },
    { id: 'marketing',    label: 'Marketing & communication', group: 'business', icon: 'marketing' },
    { id: 'training',     label: 'Formation & coaching',      group: 'business', icon: 'training' },
    { id: 'health',       label: 'Santé & paramédical',       group: 'business', icon: 'health' },

    // ---------- Fallback ----------
    { id: 'other',        label: 'Autre secteur',             group: 'business', icon: 'briefcase' },
];

/** Récupère un secteur par id, avec fallback automatique vers "other" */
export function getIndustry(id: string | null | undefined): Industry {
    if (!id) return FALLBACK_INDUSTRY;
    return INDUSTRIES.find((i) => i.id === id) ?? FALLBACK_INDUSTRY;
}

const FALLBACK_INDUSTRY = INDUSTRIES[INDUSTRIES.length - 1];

/**
 * Libellé à afficher pour un secteur :
 *  - si `id === 'other'` et `custom` non vide → `custom`
 *  - sinon → le label du registre.
 */
export function getIndustryLabel(
    id: string | null | undefined,
    custom?: string | null
): string {
    if (id === 'other' && custom && custom.trim()) {
        return custom.trim();
    }
    return getIndustry(id).label;
}

export const OTHER_INDUSTRY_ID = 'other';

/** Groupe les industries pour affichage en sections */
export function getIndustriesByGroup(): Array<{ group: IndustryGroup; items: Industry[] }> {
    return INDUSTRY_GROUPS.map((group) => ({
        group,
        items: INDUSTRIES.filter((i) => i.group === group.id),
    }));
}
